import * as React from 'react'
import * as Tooltip from 'react-tooltip'
import Select from 'react-select'
import moment from 'moment'
import { CreateOrUpdateTransfer, TransferStatus, PermissionNames } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { inject, observer } from 'mobx-react'
import { Currency } from '../../../../../components/Currency'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { TransferStore } from '../TransferStore'
import { ReactSingleSelect } from '../../../../../components/ReactSelect'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  transfer: CreateOrUpdateTransfer
  transferStore: TransferStore
  savedTransferStatus?: TransferStatus
}

interface IStatusOption {
  label: string
  value: TransferStatus
}

const getStatusOptions = (transfer: CreateOrUpdateTransfer): IStatusOption[] => {
  const pending = {
    label: 'Pending',
    value: TransferStatus.PENDING,
  }

  const inProgress = {
    label: 'In progress',
    value: TransferStatus.IN_PROGRESS,
  }
  if (transfer.status === TransferStatus.PENDING) {
    inProgress.label = 'In progress (inventory will be removed from the source location)'
  }

  const completed = {
    label: 'Completed (inventory will be fully checked in)',
    value: TransferStatus.COMPLETED,
  }
  const cancelled = {
    label: 'Cancelled (inventory changes will be reverted)',
    value: TransferStatus.CANCELLED,
  }

  return [pending, inProgress, completed, cancelled]
}

export const EditTransferMetaTable = inject(
  'currentCompanyStore',
  'currentUserStore',
)(
  observer((props: Props) => {
    const { transfer, transferStore, savedTransferStatus } = props
    const canEditTransfers = props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_TRANSFERS,
      props.currentCompanyStore!.company!.id,
    )
    const locations = props.currentCompanyStore!.locations || []

    const isStatusOptionDisabled = (option: IStatusOption | null): boolean => {
      switch (savedTransferStatus) {
        case TransferStatus.IN_PROGRESS:
          return option?.value === TransferStatus.PENDING
        case TransferStatus.COMPLETED:
          return option?.value !== TransferStatus.COMPLETED
        case TransferStatus.CANCELLED:
          return option?.value !== TransferStatus.CANCELLED
      }

      if (option === null || !savedTransferStatus) {
        return false
      }

      const batchTrackedProductExists = transfer.products.some((product) => product.isBatchTracked)

      if (!batchTrackedProductExists) {
        return false
      }

      return false
    }

    return (
      <table className='table table-sm'>
        <tbody>
          {transfer.id && (
            <tr>
              <td>ID</td>
              <td className='text-muted'>{transfer.id}</td>
            </tr>
          )}

          <tr>
            <td>Title</td>
            <td>
              <input
                className='form-control'
                value={transfer.title}
                disabled={!canEditTransfers}
                onChange={(e) => {
                  transfer.title = e.target.value
                  transferStore.hasUnsavedChanges = true
                }}
              />
            </td>
          </tr>

          <tr>
            <td>Source</td>
            <td>
              <Select
                value={transfer.fromLocationId}
                clearable={false}
                disabled={!canEditTransfers || !transferStore.isEditable}
                options={locations.map((s) => {
                  return {
                    value: s.id,
                    label: s.name,
                    disabled: s.id === transfer.toLocationId,
                  }
                })}
                onChange={(option: { label: string; value: number }) => {
                  if (option) {
                    transfer.fromLocationId = option.value
                    transferStore.hasUnsavedChanges = true
                  }
                }}
              />
            </td>
          </tr>

          <tr>
            <td>Destination</td>
            <td>
              <Select
                value={transfer.toLocationId}
                clearable={false}
                disabled={!canEditTransfers || !transferStore.isEditable}
                options={locations.map((s) => {
                  return {
                    value: s.id,
                    label: s.name,
                  }
                })}
                onChange={(option: { label: string; value: number }) => {
                  if (option) {
                    transfer.toLocationId = option.value
                    transferStore.hasUnsavedChanges = true

                    if (transfer.fromLocationId === transfer.toLocationId) {
                      transfer.fromLocationId = undefined
                    }
                  }
                }}
              />
            </td>
          </tr>

          <tr>
            <td>
              Status
              <span className='fa fa-info-circle text-muted ml-2' data-tip data-for='card-id' />
              <Tooltip id='card-id' place='right' effect='solid'>
                <div>You can only edit values for pending transfers.</div>
              </Tooltip>
            </td>
            <td>
              <ReactSingleSelect
                initialValue={transfer.status}
                options={getStatusOptions(transfer)}
                isDisabled={!canEditTransfers}
                onChange={(option: IStatusOption | null) => {
                  transfer.status = option ? option.value : undefined
                  transferStore.hasUnsavedChanges = true
                }}
                isOptionDisabled={isStatusOptionDisabled}
              />
            </td>
          </tr>

          <tr>
            <td>Notes</td>
            <td>
              <textarea
                className='form-control'
                rows={3}
                disabled={!canEditTransfers}
                value={transfer.notes || undefined}
                onChange={(e) => {
                  transfer.notes = e.target.value
                  transferStore.hasUnsavedChanges = true
                }}
              />
            </td>
          </tr>

          <tr>
            <td>Completed on</td>
            <td>
              {transfer.completeDate && transfer.status === 'completed'
                ? moment(transfer.completeDate).format('YYYY-MM-DD')
                : 'Will show on completion'}
            </td>
          </tr>

          <tr>
            <td>
              <span>Total</span>
              <span
                className='fa fa-info-circle text-muted ml-2'
                data-tip
                data-for='transfer-total'
              />
              <Tooltip id='transfer-total' place='right' effect='solid'>
                <div>You can optionally attach supply costs for transfers.</div>
                <div>Costs are pulled from a product's 'wholesale cost' field by default.</div>
                <div>
                  These will show up under sales reports in the 'Transfers breakdown' section.
                </div>
              </Tooltip>
            </td>
            <td>
              <Currency
                centValue={transferStore.editTransfer.products
                  .map((p) => p.total)
                  .reduce((a, b) => a + b, 0)}
              />
            </td>
          </tr>
        </tbody>
      </table>
    )
  }),
)
